import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import { CORE_VERSION } from "../../core/core";
import { lang, t } from "../i18n";
import { Icon } from "../icon";
import * as S from "../state";
import { Locked, Segmented, SettingRow, Switch } from "../widgets";

interface Devices {
  sources: { id: string; name: string }[];
  players: { id: string; name: string }[];
  can_select_player: boolean;
}

function regReason(r = ""): string {
  if (/^40[13]\b/.test(r)) return t("reg.credentials");
  if (/^\d{3} /.test(r)) return r;
  if (/websocket|connection|transport/i.test(r)) return t("reg.unreachable");
  return t("reg.unknown");
}

/** the browser's install prompt, kept from "beforeinstallprompt" */
export const installPrompt = signal<{ prompt(): Promise<void> } | null>(null);

export function Settings() {
  const [devices, setDevices] = useState<Devices | null>(null);
  const [confirm, setConfirm] = useState(false);
  const refresh = async (ask = false) => {
    if (ask) {
      // device names need a microphone permission
      const s = await navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null);
      s?.getTracks().forEach((x) => x.stop());
    }
    const r = await S.cmd({ cmd: "audio.devices" });
    if (r.ok) setDevices(r.data as unknown as Devices);
  };
  useEffect(() => void refresh(), []);
  const p = S.prefs.value;
  const st = S.settings.value;
  const langEntry = st["ui.language"];
  const langLocked = langEntry && !langEntry.edit;
  const ec = st["audio.echo_cancellation"];
  const dl = st["calls.default_account"];
  const managed = S.mode.value === "managed";

  const pick = (kind: "source" | "player" | "alert") => (e: Event) => {
    const id = (e.target as HTMLSelectElement).value;
    void S.cmd({ cmd: "audio.select", [kind]: id ? { id } : null });
    void S.setPrefs({ [kind]: id || undefined });
  };
  const select = (kind: "source" | "player" | "alert", list: { id: string; name: string }[]) => (
    <select value={p[kind] ?? ""} onChange={pick(kind)} style={{ maxWidth: "260px" }} aria-label={kind}>
      <option value="">{t("settings.default")}</option>
      {list
        .filter((d) => d.id !== "default" && d.id !== "communications")
        .map((d) => (
          <option value={d.id}>{d.name}</option>
        ))}
    </select>
  );

  return (
    <div class="page">
      <h1>{t("settings.title")}</h1>

      <h2>{t("settings.lines")}</h2>
      <div class="list">
        {S.lines.value.map((l) => [l, S.accounts.value[l.id]] as const).map(([l, a]) => (
          <SettingRow label={S.lineTitle(l.id)} sub={l.callerId ? S.prettyNumber(l.callerId) : t("dialer.line", String(l.position === 99 ? "" : l.position))}>
            <span class={a.state === "registered" ? "success-text" : a.state === "failed" ? "danger-text" : "warning-text"}>
              {a.state === "registered" ? t("settings.connected") : a.state === "failed" ? t("settings.failed", regReason(a.reason)) : t("settings.connecting")}
            </span>
          </SettingRow>
        ))}
        {S.lines.value.length > 1 && !dl?.hide && (
          <SettingRow label={t("settings.default_line")} sub={dl && !dl.edit ? <Locked /> : t("settings.default_line_sub")}>
            <select
              data-testid="default-line"
              value={S.defaultAccount.value}
              disabled={!!dl && !dl.edit}
              onChange={(e) => void S.setManagedSetting("calls.default_account", (e.target as HTMLSelectElement).value)}
            >
              {S.lines.value.map((l) => (
                <option value={l.id}>{S.lineTitle(l.id)}</option>
              ))}
            </select>
          </SettingRow>
        )}
        {managed ? (
          <div class="item">
            <div class="grow sub">{t("settings.managed", S.site.value?.prov_url.replace(/^https?:\/\//, "") ?? "")}</div>
            <button type="button" class="btn link" onClick={() => void S.syncNow()}>
              {t("settings.sync")}
            </button>
            <button type="button" class="btn link" onClick={() => setConfirm(true)}>
              {t("settings.reactivate")}
            </button>
          </div>
        ) : (
          <div class="item">
            <div class="grow" />
            <button type="button" class="btn link" onClick={() => void S.changeSetup()}>
              {t("settings.change_setup")}
            </button>
          </div>
        )}
        {confirm && (
          <div class="item" role="alertdialog" aria-label={t("settings.reactivate_q")}>
            <div class="grow">
              <div class="title">{t("settings.reactivate_q")}</div>
              <div class="sub">{t("settings.reactivate_help")}</div>
            </div>
            <button type="button" class="btn" onClick={() => setConfirm(false)}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              class="btn danger"
              onClick={() => {
                setConfirm(false);
                void S.reactivate();
              }}
            >
              {t("setup.activate")}
            </button>
          </div>
        )}
      </div>

      <h2>{t("settings.language")}</h2>
      <div class="list">
        <SettingRow label={t("settings.language")} sub={langLocked ? <Locked /> : undefined}>
          <Segmented
            label={t("settings.language")}
            value={langLocked ? lang.value : p.lang}
            options={[
              ["system", t("settings.system_language")],
              ["fr", t("lang.fr")],
              ["en", t("lang.en")],
            ]}
            onChange={(v) => {
              if (langLocked) return;
              void S.setPrefs({ lang: v });
              if (managed && langEntry && v !== "system") void S.setManagedSetting("ui.language", v);
            }}
          />
        </SettingRow>
      </div>

      <h2>{t("settings.appearance")}</h2>
      <div class="list">
        <SettingRow label={t("settings.theme")}>
          <Segmented
            label={t("settings.theme")}
            value={p.theme}
            options={[
              ["system", t("settings.system")],
              ["light", t("settings.light")],
              ["dark", t("settings.dark")],
            ]}
            onChange={(v) => void S.setPrefs({ theme: v })}
          />
        </SettingRow>
      </div>

      <h2>{t("settings.audio")}</h2>
      <div class="list">
        <SettingRow label={t("settings.microphone")}>{select("source", devices?.sources ?? [])}</SettingRow>
        {devices?.can_select_player ? (
          <>
            <SettingRow label={t("settings.speaker")}>{select("player", devices.players)}</SettingRow>
            <SettingRow label={t("settings.ring_on")}>{select("alert", devices.players)}</SettingRow>
          </>
        ) : (
          <SettingRow label={t("settings.speaker")} sub={t("settings.no_sink")}>
            <span />
          </SettingRow>
        )}
        {ec && !ec.hide && (
          <SettingRow label={t("settings.echo")} sub={!ec.edit ? <Locked /> : undefined}>
            <Switch label={t("settings.echo")} checked={ec.v !== false} disabled={!ec.edit} onChange={(v) => void S.setManagedSetting("audio.echo_cancellation", v)} />
          </SettingRow>
        )}
        <div class="item">
          <div class="grow" />
          <button type="button" class="btn link" onClick={() => void refresh(true)}>
            {t("settings.refresh")}
          </button>
        </div>
      </div>

      <h2>{t("settings.calls")}</h2>
      <div class="list">
        <SettingRow label={t("settings.dnd")}>
          <Switch label={t("settings.dnd")} checked={p.dnd} onChange={(v) => void S.setPrefs({ dnd: v })} />
        </SettingRow>
        <SettingRow label={t("settings.notifications")} sub={t("notify.allow")}>
          <Switch
            label={t("settings.notifications")}
            checked={typeof Notification !== "undefined" && Notification.permission === "granted" && p.notifications !== false}
            onChange={(v) => void (v ? S.askNotifications() : S.setPrefs({ notifications: false }))}
          />
        </SettingRow>
      </div>

      <h2>{t("settings.diagnostics")}</h2>
      <div class="list">
        <SettingRow label={t("settings.diagnostics")} sub={t("settings.diag_help")}>
          <button type="button" class="btn soft" onClick={() => (S.page.value = "diagnostics")}>
            <Icon name="stats" size={18} /> {t("settings.open_diag")}
          </button>
        </SettingRow>
      </div>

      <h2>{t("settings.about")}</h2>
      <div class="list">
        <SettingRow label={t("settings.version", CORE_VERSION)} sub={navigator.userAgent.replace(/^Mozilla\/5\.0 /, "")}>
          {installPrompt.value && (
            <button type="button" class="btn soft" onClick={() => void installPrompt.value!.prompt().then(() => (installPrompt.value = null))}>
              {t("settings.install")}
            </button>
          )}
        </SettingRow>
        <div class="item">
          <a class="btn link" href="/licenses.txt" target="_blank" rel="noopener">
            {t("settings.licenses")}
          </a>
        </div>
      </div>
    </div>
  );
}
